import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ChargingSession, Device, Page, Station, User } from '@eon/types';
import { api } from '../client';
import {
  camelizeKeys,
  normalizeDevice,
  normalizeAdminUser,
  normalizeGroup,
  normalizeGroupInvitation,
  normalizeGroupMember,
  normalizeIdTag,
  normalizeUserIdTag,
  normalizePage,
  normalizeSession,
  normalizeStation,
  normalizeSystemLog,
} from '../normalize';

export interface ListParams {
  page?: number;
  pageSize?: number;
  q?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  status?: string;
  [key: string]: string | number | boolean | undefined | null;
}

export interface IdTag {
  id: string;
  tag: string;
  name: string;
  value: string;
  status: 'active' | 'blocked' | 'expired';
  tagType: 'Physical' | 'Digital';
  isPhysical: boolean;
  isBlocked: boolean;
  ownerName?: string | null;
  ownerEmail?: string | null;
  userId?: string | null;
  parentIdTag?: string | null;
  description?: string | null;
  maxActiveTransactions: number;
  expiresAt?: string | null;
  issuedAt?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface CreateIdTagInput {
  value: string;
  name?: string;
  userId: string;
  isPhysical?: boolean;
  parentIdTag?: string;
  expiryDate?: string;
  maxActiveTransactions?: number;
  description?: string;
}

export interface UpdateIdTagInput {
  id: string;
  name?: string;
  value?: string;
  description?: string;
  expiryDate?: string;
  clearExpiry?: boolean;
  maxActiveTransactions?: number;
}

export interface Group {
  id: string;
  name: string;
  description?: string | null;
  memberCount: number;
  deviceCount: number;
  stationCount: number;
  active?: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface GroupMember {
  userId: string;
  role: string;
  name?: string;
  email?: string;
  phone?: string;
  joinedAt: string;
  allowanceKWh?: number;
}

export interface GroupInvitation {
  id: string;
  email?: string;
  phone?: string;
  role: string;
  status: string;
  expiresAt: string;
  createdAt: string;
}

export interface SystemLog {
  id?: string;
  time: string;
  level: 'debug' | 'info' | 'warn' | 'error';
  source?: string | null;
  message: string;
  fields?: Record<string, unknown> | null;
}

export interface EnergyHourlyPoint {
  hour: string;
  kWh: number;
}

export interface AdminDashboard {
  users: number;
  usersChargingNow: number;
  devices: number;
  stations: number;
  sessionsTotal: number;
  sessionsActive: number;
  energyKWh24h: number;
  energyByHour: EnergyHourlyPoint[];
  deviceCounts?: DeviceCounts;
}

export interface DeviceCounts {
  total: number;
  online: number;
  offline: number;
  faulted: number;
  charging: number;
  available: number;
  unregistered: number;
}

/** Admin dashboard aggregates from `GET /admin/dashboard`. */
export function useAdminDashboard() {
  return useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: async (): Promise<AdminDashboard> => {
      const raw = await api().get<Record<string, unknown>>('/admin/dashboard');
      const r = camelizeKeys<Record<string, unknown>>(raw);
      const usersObj = (r.users ?? {}) as Record<string, unknown>;
      const stationsObj = (r.stations ?? {}) as Record<string, unknown>;
      const sessionsObj = (r.sessions ?? {}) as Record<string, unknown>;
      const deviceCounts = isCounts(r.deviceCounts) ? r.deviceCounts : undefined;
      const num = (v: unknown) => (typeof v === 'number' ? v : 0);

      const energyRaw = Array.isArray(r.energyByHour) ? r.energyByHour : [];
      const energyByHour = energyRaw
        .map((row) => {
          const p = row as Record<string, unknown>;
          const hour = typeof p.hour === 'string' ? p.hour : '';
          const kWh = typeof p.kWh === 'number' ? p.kWh : 0;
          return hour ? { hour, kWh } : null;
        })
        .filter((p): p is EnergyHourlyPoint => p !== null);

      return {
        users: num(usersObj.total),
        usersChargingNow: num(usersObj.chargingNow),
        devices:
          deviceCounts?.total ??
          (typeof r.deviceCount === 'number' ? (r.deviceCount as number) : 0),
        stations: num(stationsObj.total),
        sessionsTotal: num(sessionsObj.total),
        sessionsActive: num(sessionsObj.active),
        energyKWh24h: num(r.energyKWh24h),
        energyByHour,
        deviceCounts,
      };
    },
    refetchInterval: 60_000,
  });
}

function isCounts(v: unknown): v is DeviceCounts {
  return !!v && typeof v === 'object' && 'total' in (v as Record<string, unknown>);
}

export interface AdminUserOrg {
  id: string;
  name: string;
  role: string;
}

export interface AdminUser extends User {
  lastLoginAt?: string | null;
  updatedAt?: string;
  language?: string;
  address?: Record<string, unknown> | null;
  hasPassword?: boolean;
  organizations?: AdminUserOrg[];
}

export interface UserIdTagRow {
  id: string;
  tag: string;
  value: string;
  name?: string;
  isBlocked: boolean;
  isPhysical: boolean;
  expiresAt?: string | null;
  createdAt: string;
}

export function useAdminUsers(params: ListParams = {}) {
  return useQuery({
    queryKey: ['admin', 'users', params],
    queryFn: async (): Promise<Page<AdminUser>> => {
      const raw = await api().get<unknown>('/admin/users', { query: params });
      return normalizePage<AdminUser>(raw, normalizeAdminUser);
    },
  });
}

export function useAdminUser(userId: string) {
  return useQuery({
    queryKey: ['admin', 'users', userId],
    queryFn: async (): Promise<AdminUser> => {
      const raw = await api().get<unknown>(`/admin/users/${userId}`);
      return normalizeAdminUser(raw);
    },
    enabled: !!userId,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      surname?: string;
      email: string;
      phone?: string;
      password: string;
      role?: string;
      appType?: string;
      active?: boolean;
      language?: string;
      address?: Record<string, unknown>;
    }) => api().post<AdminUser>('/admin/users', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      name?: string;
      surname?: string;
      email?: string;
      phone?: string;
      role?: string;
      appType?: string;
      active?: boolean;
      language?: string;
      address?: Record<string, unknown>;
      password?: string;
    }) => {
      const { id, ...body } = input;
      return api().patch<AdminUser>(`/admin/users/${id}`, body);
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      qc.invalidateQueries({ queryKey: ['admin', 'users', vars.id] });
    },
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api().delete(`/admin/users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export function useAdminUserIdTags(userId: string) {
  return useQuery({
    queryKey: ['admin', 'users', userId, 'id-tags'],
    queryFn: async (): Promise<UserIdTagRow[]> => {
      const raw = await api().get<unknown>(`/admin/users/${userId}/id-tags`);
      const obj = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
      const items = Array.isArray(obj.data) ? obj.data : [];
      return items.map(normalizeUserIdTag);
    },
    enabled: !!userId,
  });
}

export function useAdminUserSessions(userId: string, params: ListParams = {}) {
  return useQuery({
    queryKey: ['admin', 'users', userId, 'sessions', params],
    queryFn: async (): Promise<Page<ChargingSession>> => {
      const raw = await api().get<unknown>(`/admin/users/${userId}/sessions`, { query: params });
      return normalizePage<ChargingSession>(raw, normalizeSession);
    },
    enabled: !!userId,
  });
}

export interface InviteOrganizationResult {
  message: string;
  organizationId?: string;
  invitationId?: string;
  emailSent?: boolean;
  emailDelivery?: 'smtp' | 'noop' | string;
}

export function useInviteOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      organizationName: string;
      organizationEmail: string;
      organizationPerson: string;
      customMessage?: string;
    }) => api().post<InviteOrganizationResult>('/admin/users/invite-organization', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'groups'] });
    },
  });
}

export function useAdminDevices(params: ListParams = {}) {
  return useQuery({
    queryKey: ['admin', 'devices', params],
    queryFn: async (): Promise<Page<Device>> => {
      const raw = await api().get<unknown>('/admin/devices', { query: params });
      return normalizePage<Device>(raw, normalizeDevice);
    },
  });
}

export function useAdminDevice(id: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'devices', id],
    queryFn: async () => {
      const raw = await api().get<unknown>(`/admin/devices/${id}`);
      return normalizeDevice(raw);
    },
    enabled: Boolean(id),
  });
}

export function useAdminDeviceCounts() {
  return useQuery({
    queryKey: ['admin', 'devices', 'counts'],
    queryFn: async (): Promise<DeviceCounts> => {
      const raw = await api().get<unknown>('/admin/devices/counts');
      const r = camelizeKeys<Record<string, unknown>>(raw);
      const n = (k: string) => (typeof r[k] === 'number' ? (r[k] as number) : 0);
      return {
        total: n('total'),
        online: n('online'),
        offline: n('offline'),
        faulted: n('faulted'),
        charging: n('charging'),
        available: n('available'),
        unregistered: n('unregistered'),
      };
    },
    refetchInterval: 30_000,
  });
}

export interface CreateDeviceInput {
  serialNumber: string;
  displayName?: string;
  description?: string;
  type?: string;
  model?: string;
  vendor?: string;
  chargePointVendor?: string;
  chargePointModel?: string;
  chargeBoxSerialNumber?: string;
  firmwareVersion?: string;
  ocppVersion?: string;
  meterType?: string;
  meterSerialNumber?: string;
  organizationId?: string;
  stationId?: string;
  registered?: boolean;
  isActive?: boolean;
}

export function useCreateDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateDeviceInput) => {
      const raw = await api().post<unknown>('/admin/devices', body);
      return normalizeDevice(raw);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'devices'] });
      qc.invalidateQueries({ queryKey: ['admin', 'devices', 'counts'] });
      qc.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
    },
  });
}

export function useUpdateDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; patch: Record<string, unknown> }) =>
      api().patch<Device>(`/admin/devices/${input.id}`, input.patch),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'devices'] });
      qc.invalidateQueries({ queryKey: ['admin', 'devices', vars.id] });
    },
  });
}

export function useDeleteDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api().delete(`/admin/devices/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'devices'] }),
  });
}

export interface RegisterDeviceInput {
  id: string;
  displayName?: string;
  organizationId?: string;
  stationId?: string;
  isActive?: boolean;
  isPublic?: boolean;
}

export function useRegisterDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: RegisterDeviceInput) =>
      api().post<Device>(`/admin/devices/${id}/register`, patch),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'devices'] });
      qc.invalidateQueries({ queryKey: ['admin', 'devices', vars.id] });
    },
  });
}

export function useAdminStations(params: ListParams = {}) {
  return useQuery({
    queryKey: ['admin', 'stations', params],
    queryFn: async (): Promise<Page<Station>> => {
      const raw = await api().get<unknown>('/admin/stations', { query: params });
      return normalizePage<Station>(raw, normalizeStation);
    },
  });
}

export function useAdminStation(id: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'stations', id],
    queryFn: async () => {
      const raw = await api().get<unknown>(`/admin/stations/${id}`);
      return normalizeStation(raw);
    },
    enabled: Boolean(id),
  });
}

export interface CreateStationInput {
  name: string;
  description?: string;
  organizationId?: string;
  address: string;
  city?: string;
  country?: string;
  latitude: number;
  longitude: number;
  type?: string;
  status?: string;
  capacity?: number;
  installationDate?: string;
  displayOnMap?: boolean;
  isPublic?: boolean;
  isActive?: boolean;
  contact?: Record<string, unknown>;
  operatingHours?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

export function useCreateStation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateStationInput) => {
      const raw = await api().post<unknown>('/admin/stations', body);
      return normalizeStation(raw);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'stations'] });
      qc.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
    },
  });
}

export function useUpdateStation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; patch: Record<string, unknown> }) => {
      const raw = await api().patch<unknown>(`/admin/stations/${input.id}`, input.patch);
      return normalizeStation(raw);
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'stations'] });
      qc.invalidateQueries({ queryKey: ['admin', 'stations', vars.id] });
    },
  });
}

export function useDeleteStation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api().delete(`/admin/stations/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'stations'] });
      qc.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
    },
  });
}

export function useAddStationDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stationId, deviceId }: { stationId: string; deviceId: string }) =>
      api().post(`/admin/stations/${stationId}/devices`, { deviceId }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'stations', vars.stationId] });
      qc.invalidateQueries({ queryKey: ['admin', 'stations'] });
      qc.invalidateQueries({ queryKey: ['admin', 'devices'] });
      qc.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
    },
  });
}

export function useRemoveStationDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stationId, deviceId }: { stationId: string; deviceId: string }) =>
      api().delete(`/admin/stations/${stationId}/devices/${deviceId}`),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'stations', vars.stationId] });
      qc.invalidateQueries({ queryKey: ['admin', 'stations'] });
      qc.invalidateQueries({ queryKey: ['admin', 'devices'] });
      qc.invalidateQueries({ queryKey: ['admin', 'devices', vars.deviceId] });
    },
  });
}

export function useAdminSessions(params: ListParams = {}) {
  return useQuery({
    queryKey: ['admin', 'sessions', params],
    queryFn: async (): Promise<Page<ChargingSession>> => {
      const raw = await api().get<unknown>('/admin/charging-sessions', {
        query: params,
      });
      return normalizePage<ChargingSession>(raw, normalizeSession);
    },
    refetchInterval: 15_000,
  });
}

export function useRemoteStart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { deviceId: string; connectorId: number; idTag: string }) =>
      api().post(`/admin/devices/${input.deviceId}/remote-start`, {
        connectorId: input.connectorId,
        idTag: input.idTag,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'sessions'] }),
  });
}

/**
 * Remote-stop a charging session. `transactionId` is optional: when omitted
 * the backend resolves the active session on the device (optionally narrowed
 * by `connectorId`) — same one-click UX as the mobile app.
 */
export function useRemoteStop() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { deviceId: string; transactionId?: number; connectorId?: number }) =>
      api().post(`/admin/devices/${input.deviceId}/remote-stop`, {
        ...(input.transactionId != null && input.transactionId > 0
          ? { transactionId: input.transactionId }
          : {}),
        ...(input.connectorId != null ? { connectorId: input.connectorId } : {}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'sessions'] });
      qc.invalidateQueries({ queryKey: ['admin', 'devices'] });
    },
  });
}

export function useResetDevice() {
  return useMutation({
    mutationFn: (input: { deviceId: string; type: 'Soft' | 'Hard' }) =>
      api().post(`/admin/devices/${input.deviceId}/reset`, { type: input.type }),
  });
}

export function useChangeAvailability() {
  return useMutation({
    mutationFn: (input: {
      deviceId: string;
      connectorId: number;
      type: 'Operative' | 'Inoperative';
    }) =>
      api().post(`/admin/devices/${input.deviceId}/change-availability`, {
        connectorId: input.connectorId,
        type: input.type,
      }),
  });
}

export function useUnlockConnector() {
  return useMutation({
    mutationFn: (input: { deviceId: string; connectorId: number }) =>
      api().post(`/admin/devices/${input.deviceId}/unlock-connector`, {
        connectorId: input.connectorId,
      }),
  });
}

export function useClearCache() {
  return useMutation({
    mutationFn: (input: { deviceId: string }) =>
      api().post(`/admin/devices/${input.deviceId}/clear-cache`, {}),
  });
}

export type TriggerMessageKind =
  | 'BootNotification'
  | 'DiagnosticsStatusNotification'
  | 'FirmwareStatusNotification'
  | 'Heartbeat'
  | 'MeterValues'
  | 'StatusNotification';

export function useTriggerMessage() {
  return useMutation({
    mutationFn: (input: {
      deviceId: string;
      requestedMessage: TriggerMessageKind;
      connectorId?: number;
    }) =>
      api().post(`/admin/devices/${input.deviceId}/trigger-message`, {
        requestedMessage: input.requestedMessage,
        connectorId: input.connectorId,
      }),
  });
}

export interface OcppConfigurationKey {
  key: string;
  value?: string;
  readonly: boolean;
}

export function useGetConfiguration() {
  return useMutation({
    mutationFn: (input: { deviceId: string; keys?: string[] }) =>
      api().post<{ configurationKey?: OcppConfigurationKey[]; unknownKey?: string[] }>(
        `/admin/devices/${input.deviceId}/get-configuration`,
        { key: input.keys ?? [] },
      ),
  });
}

export function useChangeConfiguration() {
  return useMutation({
    mutationFn: (input: { deviceId: string; key: string; value: string }) =>
      api().post(`/admin/devices/${input.deviceId}/change-configuration`, {
        key: input.key,
        value: input.value,
      }),
  });
}

export function useClearChargingProfile() {
  return useMutation({
    mutationFn: (input: {
      deviceId: string;
      id?: number;
      connectorId?: number;
      chargingProfilePurpose?: string;
      stackLevel?: number;
    }) =>
      api().post(`/admin/devices/${input.deviceId}/clear-charging-profile`, {
        id: input.id,
        connectorId: input.connectorId,
        chargingProfilePurpose: input.chargingProfilePurpose,
        stackLevel: input.stackLevel,
      }),
  });
}

export function useSetChargingProfile() {
  return useMutation({
    mutationFn: (input: {
      deviceId: string;
      connectorId: number;
      // Caller passes the full csChargingProfiles payload as-is.
      csChargingProfiles: Record<string, unknown>;
    }) =>
      api().post(`/admin/devices/${input.deviceId}/set-charging-profile`, {
        connectorId: input.connectorId,
        csChargingProfiles: input.csChargingProfiles,
      }),
  });
}

export function useGetLocalListVersion() {
  return useMutation({
    mutationFn: (input: { deviceId: string }) =>
      api().post<{ listVersion: number }>(
        `/admin/devices/${input.deviceId}/get-local-list-version`,
        {},
      ),
  });
}

export type LocalAuthEntry = {
  idTag: string;
  status?: 'Accepted' | 'Blocked' | 'Expired' | 'Invalid' | 'ConcurrentTx';
  /** When omitted on Differential update => delete this entry. */
  delete?: boolean;
};

export function useSendLocalList() {
  return useMutation({
    mutationFn: (input: {
      deviceId: string;
      listVersion: number;
      entries: LocalAuthEntry[];
      updateType: 'Full' | 'Differential';
    }) =>
      api().post(`/admin/devices/${input.deviceId}/send-local-list`, {
        listVersion: input.listVersion,
        updateType: input.updateType,
        localAuthorizationList: input.entries.map((e) =>
          e.delete
            ? { idTag: e.idTag }
            : { idTag: e.idTag, idTagInfo: { status: e.status ?? 'Accepted' } },
        ),
      }),
  });
}

export function useAdminGroups(params: ListParams = {}) {
  return useQuery({
    queryKey: ['admin', 'groups', params],
    queryFn: async (): Promise<Page<Group>> => {
      const raw = await api().get<unknown>('/admin/groups', { query: params });
      return normalizePage<Group>(raw, normalizeGroup);
    },
  });
}

export function useAdminGroup(groupId: string) {
  return useQuery({
    queryKey: ['admin', 'groups', groupId],
    queryFn: async (): Promise<Group> => {
      const raw = await api().get<unknown>(`/admin/groups/${groupId}`);
      return normalizeGroup(raw);
    },
    enabled: !!groupId,
  });
}

export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; description?: string }) =>
      api().post<Group>('/admin/groups', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'groups'] }),
  });
}

export function useUpdateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      name?: string;
      description?: string;
      active?: boolean;
    }) =>
      api().patch<Group>(`/admin/groups/${input.id}`, {
        name: input.name,
        description: input.description,
        active: input.active,
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'groups'] });
      qc.invalidateQueries({ queryKey: ['admin', 'groups', vars.id] });
    },
  });
}

export function useDeleteGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api().delete(`/admin/groups/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'groups'] }),
  });
}

export function useAdminGroupMembers(groupId: string) {
  return useQuery({
    queryKey: ['admin', 'groups', groupId, 'members'],
    queryFn: async (): Promise<GroupMember[]> => {
      const raw = await api().get<unknown>(`/admin/groups/${groupId}/members`);
      const obj = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
      const items = Array.isArray(obj.data) ? obj.data : [];
      return items.map(normalizeGroupMember);
    },
    enabled: !!groupId,
  });
}

export function useAddGroupMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { groupId: string; userId: string; role?: string }) =>
      api().post(`/admin/groups/${input.groupId}/members`, {
        userId: input.userId,
        role: input.role,
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'groups', vars.groupId, 'members'] });
      qc.invalidateQueries({ queryKey: ['admin', 'groups', vars.groupId] });
      qc.invalidateQueries({ queryKey: ['admin', 'groups'] });
    },
  });
}

export function useUpdateGroupMemberRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { groupId: string; userId: string; role: string }) =>
      api().patch(`/admin/groups/${input.groupId}/members/${input.userId}`, {
        role: input.role,
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'groups', vars.groupId, 'members'] });
    },
  });
}

export function useRemoveGroupMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { groupId: string; userId: string }) =>
      api().delete(`/admin/groups/${input.groupId}/members/${input.userId}`),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'groups', vars.groupId, 'members'] });
      qc.invalidateQueries({ queryKey: ['admin', 'groups', vars.groupId] });
      qc.invalidateQueries({ queryKey: ['admin', 'groups'] });
    },
  });
}

export function useAdminGroupInvitations(groupId: string, params: ListParams = {}) {
  return useQuery({
    queryKey: ['admin', 'groups', groupId, 'invitations', params],
    queryFn: async (): Promise<Page<GroupInvitation>> => {
      const raw = await api().get<unknown>(`/admin/groups/${groupId}/invitations`, {
        query: params,
      });
      return normalizePage<GroupInvitation>(raw, normalizeGroupInvitation);
    },
    enabled: !!groupId,
  });
}

export function useCreateGroupInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      groupId: string;
      email?: string;
      phoneNumber?: string;
      role?: string;
    }) =>
      api().post<GroupInvitation>(`/admin/groups/${input.groupId}/invitations`, {
        email: input.email,
        phoneNumber: input.phoneNumber,
        role: input.role,
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'groups', vars.groupId, 'invitations'] });
    },
  });
}

export function useCancelGroupInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { groupId: string; invitationId: string }) =>
      api().delete(`/admin/groups/${input.groupId}/invitations/${input.invitationId}`),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'groups', vars.groupId, 'invitations'] });
    },
  });
}

export function useAdminGroupDevices(groupId: string, params: ListParams = {}) {
  return useQuery({
    queryKey: ['admin', 'groups', groupId, 'devices', params],
    queryFn: async (): Promise<Page<Device>> => {
      const raw = await api().get<unknown>(`/admin/groups/${groupId}/devices`, { query: params });
      return normalizePage<Device>(raw, normalizeDevice);
    },
    enabled: !!groupId,
  });
}

export function useAssignGroupDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { groupId: string; deviceId: string }) =>
      api().post(`/admin/groups/${input.groupId}/devices`, { deviceId: input.deviceId }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'groups', vars.groupId, 'devices'] });
      qc.invalidateQueries({ queryKey: ['admin', 'groups', vars.groupId] });
      qc.invalidateQueries({ queryKey: ['admin', 'groups'] });
      qc.invalidateQueries({ queryKey: ['admin', 'devices'] });
    },
  });
}

export function useUnassignGroupDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { groupId: string; deviceId: string }) =>
      api().delete(`/admin/groups/${input.groupId}/devices/${input.deviceId}`),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'groups', vars.groupId, 'devices'] });
      qc.invalidateQueries({ queryKey: ['admin', 'groups', vars.groupId] });
      qc.invalidateQueries({ queryKey: ['admin', 'groups'] });
      qc.invalidateQueries({ queryKey: ['admin', 'devices'] });
    },
  });
}

export function useAdminGroupStations(groupId: string, params: ListParams = {}) {
  return useQuery({
    queryKey: ['admin', 'groups', groupId, 'stations', params],
    queryFn: async (): Promise<Page<Station>> => {
      const raw = await api().get<unknown>(`/admin/groups/${groupId}/stations`, { query: params });
      return normalizePage<Station>(raw, normalizeStation);
    },
    enabled: !!groupId,
  });
}

export function useCreateGroupStation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      groupId: string;
      name: string;
      description?: string;
      address?: string;
      latitude?: number;
      longitude?: number;
      type?: string;
      status?: string;
      capacity?: number;
      displayOnMap?: boolean;
    }) => api().post<Station>(`/admin/groups/${input.groupId}/stations`, input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'groups', vars.groupId, 'stations'] });
      qc.invalidateQueries({ queryKey: ['admin', 'groups', vars.groupId] });
      qc.invalidateQueries({ queryKey: ['admin', 'groups'] });
      qc.invalidateQueries({ queryKey: ['admin', 'stations'] });
    },
  });
}

/** Fetches every admin id-tag page (legacy getAllIdTagsAll parity). */
export async function fetchAllAdminIdTags(
  params: Omit<ListParams, 'page' | 'pageSize'> = {},
  pageSize = 100,
): Promise<IdTag[]> {
  const all: IdTag[] = [];
  let page = 1;
  for (;;) {
    const raw = await api().get<unknown>('/admin/id-tags', {
      query: { ...params, page, pageSize },
    });
    const result = normalizePage<IdTag>(raw, normalizeIdTag);
    all.push(...result.data);
    if (page >= result.totalPages) break;
    page++;
  }
  return all;
}

export function useAllAdminIdTags(params: Omit<ListParams, 'page' | 'pageSize'> = {}) {
  return useQuery({
    queryKey: ['admin', 'id-tags', 'all', params],
    queryFn: () => fetchAllAdminIdTags(params),
    staleTime: 60_000,
  });
}

export function useAdminIdTags(params: ListParams = {}) {
  return useQuery({
    queryKey: ['admin', 'id-tags', params],
    queryFn: async (): Promise<Page<IdTag>> => {
      const raw = await api().get<unknown>('/admin/id-tags', { query: params });
      return normalizePage<IdTag>(raw, normalizeIdTag);
    },
  });
}

export function useAdminIdTag(id: string) {
  return useQuery({
    queryKey: ['admin', 'id-tags', id],
    queryFn: async (): Promise<IdTag> => {
      const raw = await api().get<unknown>(`/admin/id-tags/${id}`);
      return normalizeIdTag(raw);
    },
    enabled: Boolean(id),
  });
}

export function useCreateIdTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateIdTagInput) => api().post<IdTag>('/admin/id-tags', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'id-tags'] }),
  });
}

export function useUpdateIdTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateIdTagInput) => {
      const { id, ...body } = input;
      return api().patch<IdTag>(`/admin/id-tags/${id}`, body);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'id-tags'] }),
  });
}

export function useBlockIdTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api().post(`/admin/id-tags/${id}/block`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'id-tags'] }),
  });
}

export function useUnblockIdTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api().post(`/admin/id-tags/${id}/unblock`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'id-tags'] }),
  });
}

export function useDeleteIdTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api().delete(`/admin/id-tags/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'id-tags'] }),
  });
}

export function useSystemLogs(params: ListParams = {}) {
  return useQuery({
    queryKey: ['admin', 'logs', params],
    queryFn: async (): Promise<Page<SystemLog>> => {
      const raw = await api().get<unknown>('/admin/system-logs', { query: params });
      return normalizePage<SystemLog>(raw, normalizeSystemLog);
    },
    refetchInterval: 10_000,
  });
}
